import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import CounterfindDashboardHeader from './CounterfindDashboardHeader';
import {
  ShieldCheck,
  Search,
  Gavel,
  AlertTriangle,
  UploadCloud,
  Link as LinkIcon,
  FileText,
  Loader2,
  Send,
  BarChart3,
  TrendingUp,
  X,
} from 'lucide-react';

/**
 * Custom dashboard for the `counterfind@operatr.com` tenant when the sidebar
 * role is set to `client`. Shows:
 *   1. Three KPI containers (Active investigations, Counterfeits found, Takedowns completed)
 *   2. Submit Case form (Product URL, Screenshot upload, Notes)
 *   3. Reports container (Analytics + Platform trends)
 *
 * Self-contained: takes `userId`, talks directly to /api/counterfind/*.
 */
const CounterfindClientDashboard = ({ userId }) => {
  const apiBase = window.location.origin;
  const [stats, setStats] = useState({
    active_investigations: 0,
    counterfeits_found: 0,
    takedowns_completed: 0,
    total_cases: 0,
  });
  const [reports, setReports] = useState({ by_platform: [], monthly: [] });
  const [loading, setLoading] = useState(true);

  // Submit case form state
  const [productUrl, setProductUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        fetch(`${apiBase}/api/counterfind/stats?user_id=${encodeURIComponent(userId)}`).then(x => x.json()),
        fetch(`${apiBase}/api/counterfind/reports?user_id=${encodeURIComponent(userId)}`).then(x => x.json()),
      ]);
      setStats(s);
      setReports(r);
    } catch (e) {
      console.error('Counterfind fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [userId]); // eslint-disable-line

  const handleScreenshotChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Screenshot must be under 8 MB');
      return;
    }
    setScreenshot(f);
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const clearScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const submitCase = async (e) => {
    e.preventDefault();
    if (!productUrl.trim()) {
      toast.error('Product URL is required.');
      return;
    }
    if (!userId) {
      toast.error('User not loaded yet — try again in a moment.');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('user_id', userId);
      form.append('product_url', productUrl.trim());
      form.append('notes', notes.trim());
      if (screenshot) form.append('screenshot', screenshot);

      const res = await fetch(`${apiBase}/api/counterfind/cases`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      toast.success('Case submitted — our team will investigate.');
      setProductUrl('');
      setNotes('');
      clearScreenshot();
      fetchAll();
    } catch (err) {
      console.error('Case submit failed', err);
      toast.error(err.message || 'Failed to submit case');
    } finally {
      setSubmitting(false);
    }
  };

  // Compute max for the bar charts (avoid div by zero)
  const maxPlatform = Math.max(1, ...reports.by_platform.map(p => p.total));
  const maxMonthly = Math.max(1, ...reports.monthly.map(m => m.cases));

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-client-dashboard">
      <div className="max-w-[1800px] mx-auto">
        <CounterfindDashboardHeader roleLabel="Client" />
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <ShieldCheck size={28} className="text-emerald-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Counterfind</h1>
          </div>
          <p className="text-gray-400 text-sm">Brand-protection client dashboard</p>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard
            icon={Search}
            tone="amber"
            label="Active Investigations"
            value={stats.active_investigations}
            sub="Submitted + in research"
            loading={loading}
            testId="kpi-active-investigations"
          />
          <StatCard
            icon={AlertTriangle}
            tone="rose"
            label="Counterfeits Found"
            value={stats.counterfeits_found}
            sub="Confirmed cases"
            loading={loading}
            testId="kpi-counterfeits-found"
          />
          <StatCard
            icon={Gavel}
            tone="emerald"
            label="Takedowns Completed"
            value={stats.takedowns_completed}
            sub="Filed & enforced"
            loading={loading}
            testId="kpi-takedowns-completed"
          />
        </div>

        {/* Submit + Reports row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Submit Case */}
          <div
            className="bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/30 rounded-2xl p-5"
            data-testid="counterfind-submit-case"
          >
            <div className="flex items-center gap-2 mb-4">
              <Send size={18} className="text-emerald-400" />
              <h2 className="text-lg font-semibold">Submit Case</h2>
            </div>
            <form onSubmit={submitCase} className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Product URL *</label>
                <div className="relative">
                  <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="url"
                    required
                    value={productUrl}
                    onChange={(e) => setProductUrl(e.target.value)}
                    placeholder="https://example.com/suspect-listing"
                    data-testid="counterfind-product-url"
                    className="w-full bg-black border border-gray-700 rounded-lg pl-9 pr-3 py-2.5 text-white placeholder-gray-600 focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Screenshot</label>
                {screenshotPreview ? (
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt="Screenshot preview"
                      className="w-full max-h-48 object-contain rounded-lg border border-gray-700 bg-black"
                    />
                    <button
                      type="button"
                      onClick={clearScreenshot}
                      data-testid="counterfind-screenshot-remove"
                      className="absolute top-2 right-2 p-1 bg-black/80 hover:bg-rose-500/80 border border-gray-600 rounded-full text-white transition-colors"
                      aria-label="Remove screenshot"
                    >
                      <X size={14} />
                    </button>
                    <div className="text-xs text-gray-400 mt-1 truncate">
                      {screenshot?.name} · {(screenshot?.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="counterfind-screenshot-input"
                    className="flex flex-col items-center justify-center gap-1 w-full py-5 border-2 border-dashed border-gray-700 hover:border-emerald-500/60 rounded-lg cursor-pointer text-gray-400 hover:text-white transition-colors"
                  >
                    <UploadCloud size={20} />
                    <span className="text-xs">Click to upload (JPG / PNG / WebP / PDF — max 8 MB)</span>
                    <input
                      id="counterfind-screenshot-input"
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={handleScreenshotChange}
                      data-testid="counterfind-screenshot-input"
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Notes</label>
                <div className="relative">
                  <FileText size={16} className="absolute left-3 top-3 text-gray-500" />
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Brand, SKU, suspected reason, urgency — anything that helps us investigate."
                    data-testid="counterfind-notes"
                    className="w-full bg-black border border-gray-700 rounded-lg pl-9 pr-3 py-2.5 text-white placeholder-gray-600 focus:border-emerald-500 focus:outline-none transition-colors text-sm resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !productUrl.trim()}
                data-testid="counterfind-submit-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Submitting…</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>Submit Case</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Reports */}
          <div
            className="bg-gradient-to-br from-cyan-500/5 to-transparent border border-cyan-500/30 rounded-2xl p-5"
            data-testid="counterfind-reports"
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-cyan-400" />
              <h2 className="text-lg font-semibold">Reports</h2>
            </div>

            {/* Analytics — monthly cases */}
            <div className="mb-5" data-testid="counterfind-reports-analytics">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-gray-400">Analytics — last 6 mo</span>
                <span className="text-xs text-gray-500">{stats.total_cases} total cases</span>
              </div>
              {reports.monthly.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-6 text-center border border-gray-800 rounded-lg">
                  No case history yet. Submit your first case on the left.
                </div>
              ) : (
                <div className="flex items-end gap-2 h-24">
                  {reports.monthly.map((m) => (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="w-full bg-gray-900 rounded-t relative overflow-hidden h-20">
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-cyan-500/70 rounded-t"
                          style={{ height: `${(m.cases / maxMonthly) * 100}%` }}
                          title={`${m.cases} cases (${m.takedowns} takedowns)`}
                        />
                      </div>
                      <span className="text-[10px] text-gray-500 truncate w-full text-center">{m.month.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Platform trends */}
            <div data-testid="counterfind-reports-platforms">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-gray-400">Platform trends</span>
                <TrendingUp size={14} className="text-cyan-400" />
              </div>
              {reports.by_platform.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-4 text-center border border-gray-800 rounded-lg">
                  No platforms yet — once you submit cases we'll group them here.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {reports.by_platform.map((p) => (
                    <div key={p.platform} className="flex items-center gap-3 text-sm">
                      <div className="w-20 truncate text-gray-300" title={p.platform}>{p.platform}</div>
                      <div className="flex-1 bg-gray-900 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                          style={{ width: `${(p.total / maxPlatform) * 100}%` }}
                        />
                      </div>
                      <div className="w-20 text-right text-xs text-gray-400">
                        {p.takedowns}/{p.total} <span className="text-gray-600">Takedowns</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const toneClasses = {
  amber: {
    ring: 'border-amber-500/30 from-amber-500/10',
    icon: 'text-amber-400 bg-amber-500/20',
  },
  rose: {
    ring: 'border-rose-500/30 from-rose-500/10',
    icon: 'text-rose-400 bg-rose-500/20',
  },
  emerald: {
    ring: 'border-emerald-500/30 from-emerald-500/10',
    icon: 'text-emerald-400 bg-emerald-500/20',
  },
};

const StatCard = ({ icon: Icon, tone = 'emerald', label, value, sub, loading, testId }) => {
  const t = toneClasses[tone] || toneClasses.emerald;
  return (
    <div
      className={`bg-gradient-to-br ${t.ring.split(' ')[1]} to-transparent border ${t.ring.split(' ')[0]} rounded-2xl p-5`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${t.icon}`}>
          <Icon size={20} />
        </div>
        {loading && <Loader2 size={14} className="text-gray-500 animate-spin" />}
      </div>
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-sm font-medium text-gray-300 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
};

export default CounterfindClientDashboard;
